import { viewFile } from '@/src/components/dashboard/pdf-pages';
// What a task can point at, the button it shows, and what tapping it does.
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';

export type TaskLink =
  | { type: 'quiz'; course_id: string; label: string }
  | { type: 'document'; bucket: string; name: string; label: string }
  | { type: 'eod_report'; label: string }
  | { type: 'nightly_numbers'; label: string };

export const DOC_BUCKETS = ['sops', 'menu-images', 'training-videos'];

export function linkButtonLabel(l: TaskLink): string {
  switch (l.type) {
    case 'quiz': return 'Take the quiz';
    case 'document': return 'Open document';
    case 'eod_report': return 'Write EOD report';
    case 'nightly_numbers': return 'Do nightly numbers';
  }
}

export const docLabel = (name: string) => name.replace(/^\d{10,}-/, '').replace(/\.[a-z0-9]+$/i, '').replace(/_/g, ' ');

/** Opens what the task links to: a document in a new tab, anything else by switching page in the app. */
export async function openTaskLink(l: TaskLink, venueId: string): Promise<string | null> {
  if (l.type === 'document') {
    const path = `${venueId}/${l.name}`;
    const { data, error } = await supabase.storage.from(l.bucket).createSignedUrl(path, 600);
    if (error || !data?.signedUrl) return 'That document could not be opened. It may have been renamed or removed.';
    viewFile(data.signedUrl, docLabel(l.name));
    return null;
  }
  const detail = l.type === 'quiz' ? { page: 'training', courseId: l.course_id }
    : l.type === 'eod_report' ? { page: 'eod', view: 'submit' }
    : { page: 'eod', view: 'numbers' };
  window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail }));
  return null;
}

/** Tick off the person's open tasks that point at something they just finished. Best-effort.
 *  "Each person" tasks: ticks their own part. Shared tasks: completes the task. */
export async function completeLinkedTasks(userId: string, match: { type: TaskLink['type']; course_id?: string }) {
  const now = new Date().toISOString();
  let q = supabase.from('tasks').select('id, mode, task_assignees!inner(user_id)').neq('status', 'done').eq('link->>type', match.type).eq('task_assignees.user_id', userId);
  if (match.course_id) q = q.eq('link->>course_id', match.course_id);
  const { data, error } = await q;
  if (error) {
    // Before the task-rework update: one person per task.
    let old = supabase.from('tasks').update({ status: 'done', completed_at: now }).eq('assigned_to', userId).neq('status', 'done').eq('link->>type', match.type);
    if (match.course_id) old = old.eq('link->>course_id', match.course_id);
    const r = await old;
    if (r.error && !/link/.test(r.error.message)) log.warn('linked task completion failed', { code: r.error.code });
    return;
  }
  for (const t of (data ?? []) as Array<{ id: string; mode: string }>) {
    if (t.mode === 'individual') await supabase.from('task_assignees').update({ completed_at: now }).eq('task_id', t.id).eq('user_id', userId);
    else await supabase.from('tasks').update({ status: 'done', completed_at: now }).eq('id', t.id);
  }
}

/** Documents a task can link to (all document buckets for the venue). */
export async function listLinkableDocuments(venueId: string): Promise<Array<{ bucket: string; name: string; label: string }>> {
  const out: Array<{ bucket: string; name: string; label: string }> = [];
  await Promise.all(DOC_BUCKETS.map(async (bucket) => {
    const { data } = await supabase.storage.from(bucket).list(venueId, { limit: 200, sortBy: { column: 'name', order: 'asc' } });
    for (const f of data ?? []) if (f.name && !f.name.startsWith('.') && f.id) out.push({ bucket, name: f.name, label: docLabel(f.name) });
  }));
  return out.sort((a, b) => a.label.localeCompare(b.label));
}
